import React, { FC, useEffect, useCallback, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';

import { Node } from 'src/common/interfaces/node.interface';
import { Step } from 'src/common/interfaces/step.interface';
import { last } from 'lodash';

import JourneySlideover from 'src/common/JourneySlideOver';
import { useCurrentOrganization } from 'src/store/organization';
import { apiAddNodeToJourney, apiGetAllNodes } from 'src/utils/journeyApi';
import { useEditorContext } from 'src/store/editor';
import { TextInput } from 'src/common/form/text-input';
import { EditorContentSyncedStepCard } from './synced-step-card';
import { getAllNodes } from 'src/utils/journey';
import { useStepEditorContext } from 'src/store/step-editor';

interface ContentSyncedStepsPanelProps {
  open: boolean;
  onClose: () => void;
  step: Step;
}

export const ContentSyncedStepsPanel: FC<ContentSyncedStepsPanelProps> = ({ open, onClose, step }) => {
  const { currentOrganization } = useCurrentOrganization();
  const { journey, setJourney, setHasSyncedSteps, setCurrentActiveNodeId } = useEditorContext();
  const { setStateFromNode } = useStepEditorContext();
  const [nodes, setNodes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const getSyncedNodes = useCallback(
    (searchQuery?: string) => {
      apiGetAllNodes(currentOrganization.id, {
        is_synced: true,
        ...(searchQuery && { search_str: searchQuery }),
      }).then((data) => {
        const allJourneyNodeUuids = (getAllNodes(journey) as Node[]).map((n) => n.uuid);
        const unusedSyncedNodes = data.journey_nodes.filter((n: Node) => !allJourneyNodeUuids.includes(n.uuid));
        setNodes(unusedSyncedNodes);
        if (unusedSyncedNodes.length > 0) {
          setHasSyncedSteps(true);
        } else {
          setHasSyncedSteps(false);
        }
      });
    },
    [currentOrganization.id, journey]
  );

  useEffect(() => {
    if (open) {
      getSyncedNodes();
    }
  }, [open, getSyncedNodes]);

  const onAddSyncedNode = useCallback(
    async (node: Node, step: Step) => {
      const previousNode = last(step.nodes) || null;

      const journey = await apiAddNodeToJourney(
        node.uuid,
        step.id,
        // @ts-ignore
        previousNode ? previousNode.uuid : null
      );
      setJourney(journey);
      setCurrentActiveNodeId(node.id);
      setStateFromNode(node);
      onClose();
    },
    [setJourney, setCurrentActiveNodeId, setStateFromNode, onClose]
  );

  const debouncedSearchQueryHandler = useMemo(() => debounce((value) => getSyncedNodes(value), 200), [getSyncedNodes]);
  // Stop the invocation of the debounced function after unmounting
  useEffect(() => {
    return () => {
      debouncedSearchQueryHandler.cancel();
    };
  }, [debouncedSearchQueryHandler]);

  useEffect(() => {
    debouncedSearchQueryHandler(searchQuery);
  }, [searchQuery, debouncedSearchQueryHandler]);

  return (
    <JourneySlideover
      isBedrock={true}
      title='Use a Synced Step'
      open={open}
      maxWidth='auto'
      containerClassName='z-40'
      panelClassName='w-[368px]'
      onClose={onClose}
    >
      <div className='flex-flex-col'>
        <TextInput
          id='synced-steps-search'
          inputProps={{
            type: 'text',
            value: searchQuery,
            placeholder: 'Search your library',
          }}
          hideOptional={true}
          autoFocus={true}
          required={true}
          branded={true}
          onChange={(e: any) => setSearchQuery(e.target.value)}
        />
        <div className='grid grid-cols-1 gap-2 mt-4'>
          {nodes.map((node) => (
            <EditorContentSyncedStepCard node={node} step={step} onAddSyncedStep={() => onAddSyncedNode(node, step)} />
          ))}
        </div>
      </div>
    </JourneySlideover>
  );
};
